import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { CashRunway } from "@/components/diagrams/CashRunway";
import { ArrowRight, LineChart } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import siteOfficePlansImg from "@/assets/photos/site-office-plans.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const PATH = "/specialisations/planning-budgeting-and-forecasting";
const TITLE =
  "Business planning, budgeting and forecasting";
const DESCRIPTION =
  "Annual business planning, driver-based operating budgets, 13-week rolling cash forecasting, quarterly reforecasting and the cadence that keeps them alive.";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure:
      "Owned the annual business planning process, covering the strategic plan, budget and forecast, for a 249-store national retail network, delivering above-budget profit in FY15 and FY16",
    org: "A 249-store national retailer",
  },
  {
    figure:
      "Installed annual strategic planning and quarterly forecasting at a transport business, one of several changes made across four years in which net profit grew each year",
    org: "A transport group",
  },
  {
    figure:
      "Introduced a one-page strategic plan, weekly leadership cadence, and P&L and cashflow forecasting inside the first 90 days",
    org: "Mining services",
  },
];

const covers = [
  {
    title: "Annual business planning",
    desc: "A one-page plan with a small number of objectives the leadership team can actually name without looking it up.",
  },
  {
    title: "Driver-based operating budgets",
    desc: "A budget built from volumes, rates, headcount and utilisation rather than last year plus a percentage, so a change in the business changes the number.",
  },
  {
    title: "13-week rolling cash forecast",
    desc: "A 13-week rolling cash forecast built on actuals, with a weekly cadence to keep it current, is one of Ajility's core planning tools. Ajility builds the operational forecast and rhythm and leaves tax and accounting matters with your accountant. Cash in and cash out by week, rolled forward every week, covering the horizon where a decision can still change the outcome.",
  },
  {
    title: "Quarterly reforecasting",
    desc: "A structured reset each quarter, so the plan reflects the year as it is rather than the year as it was assumed in month one.",
  },
  {
    title: "Reporting cadence",
    desc: "Weekly, monthly and quarterly reviews with a fixed agenda, named owners and a short list of numbers, so the plan is read rather than filed.",
  },
];

const faqs = specialisationFaqs["planning-budgeting-and-forecasting"];

export const Route = createFileRoute("/specialisations/planning-budgeting-and-forecasting")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-planning-budgeting-and-forecasting", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Planning, budgeting and forecasting", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Business planning, budgeting and forecasting",
          description: DESCRIPTION,
          path: PATH,
          offerId: ["rolling-cash-forecast", "annual-operating-budget"],
          slug: "planning-budgeting-and-forecasting",
          serviceType: "Business planning and forecasting",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: PlanningSpecialisation,
});

function PlanningSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <LineChart className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">
            Expertise: planning, budgeting and forecasting
          </span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            A plan nobody tracks is a document. A plan with a budget and a forecast behind it is an
            operating system.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            Most founder-led businesses have a plan somewhere and a budget in a spreadsheet, and
            <Link to="/insights/a-plan-nobody-tracks-is-a-document" className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">neither one is consulted after February</Link>. The work here connects the plan to the money
            and the money to a cadence, so the numbers are used to make decisions during the year.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_planning_budgeting_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See the priced modules
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={siteOfficePlansImg}
        alt="Plans spread across the table in a small site office, with a hard hat and tape measure alongside"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            From the annual plan to the weekly number
          </h2>
          <div className="mt-12">
            <CashRunway />
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Planning and forecasting delivered in senior operating roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-3">
            {proof.map((p) => (
              <li key={p.figure} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Buy it on its own</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Annual Operating Budget and Rolling Cash Forecast, 13 weeks
          </h2>
          <p className="mt-4 text-charcoal">
            This area of expertise sits inside every Fractional COO engagement, and both modules can
            also be bought as single priced pieces of work. Scope and fees are published on the
            packages page.
          </p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "13-week-cash-flow-forecast" }} className="underline underline-offset-4">the 13-week cash flow forecast</Link> for the practical questions behind this work.</p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "payday-super-cash-flow" }} className="underline underline-offset-4">Payday Super: what it does to a small business's cash flow</Link> for the practical questions behind this work.</p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See both modules
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="planning-budgeting-and-forecasting" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_planning_budgeting_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
